<template>
  <div class="popular-destinations">
    <div class="popular-destinations__container">
      <div class="popular-destinations__title">{{t('Where Do You Want to Go?')}}</div>
      <div class="popular-destinations__info">
        <p v-if="$siteConfig.brand === 'Solaire'">{{t('Discover our curated collection of premium hotel and short term rental properties across premier destinations.')}}</p>
        <p v-else>{{t('From bustling metropolises to tranquil beaches, these popular destinations are perfect for your next getaway. Find inspiration for your journey here.')}}</p>
      </div>

      <div class="popular-destinations__section">
        <div class="popular-destinations__sub-title">{{t('Popular destinations')}}</div>
        <div class="popular-destinations__list">
          <nuxt-link
              class="popular-destinations__item"
              v-for="city in destinations"
              :to="localePath(city.link)"
              :key="city.title"
          >
            <div class="popular-destinations__img">
              <img :src="city.img" :alt="city.title" width="262" height="180" loading="lazy">
            </div>
            <div class="popular-destinations__name">{{city.title}}</div>
          </nuxt-link>
        </div>
      </div>

      <div v-if="topCountries.length" class="popular-destinations__section">
        <div class="popular-destinations__sub-title">{{t('Browse Hotels by Country')}}</div>
        <div class="popular-destinations__countries">
          <nuxt-link
            v-for="country in topCountries"
            :key="country.slug"
            :to="localePath(`/hotels/destinations/${country.slug}`)"
            class="popular-destinations__country-link"
          >
            {{ country.country_name }} <span class="popular-destinations__country-count">({{ country.hotel_count }})</span>
          </nuxt-link>
          <nuxt-link
            :to="localePath('/hotels/destinations')"
            class="popular-destinations__country-link popular-destinations__country-link--all"
          >
            {{t('View all destinations')}} &rarr;
          </nuxt-link>
        </div>
      </div>
    </div>
  </div>
</template>


<script lang="ts" setup>
const { t } = useI18n()
const localePath = useLocalePath();
const config = useRuntimeConfig()
const { $siteConfig } = useNuxtApp()

interface Country {
  country_code: string
  country_name: string
  slug: string
  hotel_count: number
}

const topCountries = ref<Country[]>([])

onMounted(async () => {
  try {
    const data = await $fetch<Country[]>(`${config.public.baseURL}/api/product/v1/hotels/destinations`)
    topCountries.value = (data || []).slice(0, 15)
  } catch {
    // Non-critical — the hardcoded destinations below still show
  }
})

const destinations = [
  {
    title: 'New York',
    link: '/hotels/us-ny-new-york',
    img: '/images/cities/new-york.webp',
  },
  {
    title: 'Miami',
    link: '/hotels/us-fl-miami-beach',
    img: '/images/cities/miami.webp',
  },
  {
    title: 'Los Angeles',
    link: '/hotels/us-ca-los-angeles',
    img: '/images/cities/los-angeles.webp',
  },
  {
    title: 'Las Vegas',
    link: '/hotels/us-nv-las-vegas',
    img: '/images/cities/las-vegas.webp',
  },
  {
    title: 'San Francisco',
    link: '/hotels/us-ca-san-francisco',
    img: '/images/cities/san-francisco.webp',
  },
  {
    title: 'Seattle',
    link: '/hotels/us-wa-seattle',
    img: '/images/cities/seattle.webp',
  },
  {
    title: 'San Diego',
    link: '/hotels/us-ca-san-diego',
    img: '/images/cities/san-diego.webp',
  },
  {
    title: 'Detroit',
    link: '/hotels/us-mi-detroit',
    img: '/images/cities/detroit.webp',
  }
]
</script>

<style lang="scss">
.popular-destinations {
  padding: 40px 0;


  @media screen and (max-width: 1252px) {
    padding: 40px 16px;
  }

  &__title {
    font-weight: 600;
    font-size: 34px;
    line-height: 44px;
    letter-spacing: 0;
    margin-bottom: 14px;
  }

  &__info {
    p {
      font-weight: 400;
      font-size: 22px;
      line-height: 28px;
      letter-spacing: 0;

      @media screen and (max-width: 768px) {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }

  &__container {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
  }

  &__section {
    margin-top: 40px;
  }

  &__sub-title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0;
    margin-bottom: 24px;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -8px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 25%;
    padding: 8px;

    @media screen and (max-width: 768px) {
      width: 50%;
    }
  }

  &__img {
    margin-bottom: 8px;
    border-radius: 6px;
    overflow: hidden;
    img {
      width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  &__name {
    font-weight: 600;
    font-size: 20px;
    line-height: 26px;
    letter-spacing: 0;
    text-align: center;
  }

  &__countries {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  &__country-link {
    font-size: 15px;
    font-weight: 500;
    color: var(--blue, #1985f9);
    text-decoration: none;
    padding: 6px 0;

    &:hover {
      text-decoration: underline;
    }

    &--all {
      font-weight: 600;
    }
  }

  &__country-count {
    font-weight: 400;
    color: var(--dark-grey);
    font-size: 13px;
  }
}
</style>
